import React, { useEffect, useMemo, useRef } from 'react';
import { Field as ReduxFormField } from 'redux-form';
import { useController, useFormContext } from 'react-hook-form';
import type { FieldMeta, FieldRenderProps, FieldValidator } from './types';

// ECDEX-3442 (milestone 1): the one field wrapper forms use while they move
// from redux-form to react-hook-form. Inside a react-hook-form <FormProvider>
// it is a Controller that hands the component redux-form-shaped `input` and
// `meta`; anywhere else it is redux-form's own `Field`. Either way the
// component, and the `validate` / `normalize` / `onChange` props, are the ones
// the forms already use, so a form can move engines without touching its
// fields.
//
// Under react-hook-form, build the form with useAppForm (./useAppForm) so
// validation runs the way redux-form's did.
export interface FormFieldProps {
  name: string;
  component: React.ComponentType<any>;
  type?: string;
  validate?: FieldValidator | FieldValidator[];
  normalize?: (value: any, previousValue: any, allValues: any) => any;
  // redux-form's Field onChange: called with the change before it is applied,
  // and `event.preventDefault()` stops it.
  onChange?: (
    event: any,
    newValue: any,
    previousValue: any,
    name: string,
  ) => void;
  [prop: string]: any;
}

// What redux-form's own change handler reads from a DOM event.
const valueFromEvent = (eventOrValue: any) => {
  const target = eventOrValue?.target;
  if (!target || typeof eventOrValue.preventDefault !== 'function') {
    return eventOrValue;
  }
  if (target.type === 'checkbox') return target.checked;
  if (target.type === 'file') return target.files;
  if (target.type === 'select-multiple') {
    return Array.from(target.options as ArrayLike<HTMLOptionElement>)
      .filter(option => option.selected)
      .map(option => option.value);
  }
  return target.value;
};

// react-hook-form tracks no focus state and nothing reads it.
const noop = () => undefined;

const ReactHookFormField = ({
  name,
  component: Component,
  validate,
  normalize,
  onChange: onChangeProp,
  ...rest
}: FormFieldProps) => {
  const { control, getValues, trigger, watch } = useFormContext();
  const validators = useMemo(
    () => ([] as FieldValidator[]).concat(validate ?? []),
    [validate],
  );
  // A validator can read the other fields (`allValues`), and redux-form re-ran
  // every validator on any change; react-hook-form only validates a field when
  // it changes or the form submits. So a change in another field re-validates
  // this one.
  const hasValidators = validators.length > 0;
  useEffect(() => {
    if (!hasValidators) return undefined;
    const subscription = watch((_values, { name: changed }) => {
      if (changed && changed !== name) trigger(name);
    });
    return () => subscription.unsubscribe();
  }, [hasValidators, name, trigger, watch]);

  // react-hook-form only carries a string message, so the params of an
  // `{ message, ...params }` error wait here until meta is built.
  const errorParams = useRef<Record<string, any>>({});

  const {
    field,
    fieldState,
    formState: { isSubmitted },
  } = useController({
    name,
    control,
    rules: {
      validate: (value, formValues) => {
        for (const validator of validators) {
          const result = validator(value, formValues, rest, name);
          if (result) {
            if (typeof result === 'string') {
              errorParams.current = {};
              return result;
            }
            const { message, ...params } = result;
            errorParams.current = params;
            return message ?? 'validation error';
          }
        }
        return true;
      },
    },
  });

  const message = fieldState.error?.message;
  const error =
    message && Object.keys(errorParams.current).length
      ? { message, ...errorParams.current }
      : message;

  const meta: FieldMeta = {
    // redux-form touches every field when a submit is attempted.
    touched: fieldState.isTouched || isSubmitted,
    error,
    valid: !message,
    invalid: Boolean(message),
    dirty: fieldState.isDirty,
    pristine: !fieldState.isDirty,
  };

  const onChange = (eventOrValue: any) => {
    const previousValue = field.value;
    const next = valueFromEvent(eventOrValue);
    const value = normalize
      ? normalize(next, previousValue, getValues())
      : next;
    onChangeProp?.(eventOrValue, value, previousValue, name);
    if (eventOrValue?.defaultPrevented) return;
    field.onChange(value);
  };

  const props: FieldRenderProps = {
    input: {
      name,
      value: field.value ?? '',
      onChange,
      onBlur: () => field.onBlur(),
      onFocus: noop,
    },
    meta,
  };

  return <Component {...rest} {...props} />;
};

const FormField = (props: FormFieldProps) => {
  const form = useFormContext();
  // redux-form's Field and ours take the same props.
  const Engine: React.ComponentType<any> = form
    ? ReactHookFormField
    : (ReduxFormField as any);
  return <Engine {...props} />;
};

export default FormField;
